import React, { useMemo } from "react";
import { Box, Typography, Stack, Container, Skeleton } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ROUTES from "../../router/routes.constants";
import CircleIcon from "../../assets/circleicon.svg";
import FinFactorBankCard from "../../components/common/cards/FinFactorBankCard";
import { useGetInvestmentPortfolioMutualFundsQuery, useGetInvestmentPortfolioStocksQuery, useGetInvestmentPortfolioEtfQuery, useGetInvestmentPortfolioInvitQuery, useGetInvestmentPortfolioReitQuery } from "../../store/api/finfactor.apislice";
import { useGetPortfolioSummaryQuery } from "../../store/api/portfolio.apislice";
import { parseMutualFundsBrokerListing } from "../../store/parsers/finfactor.parser";
import { getFundInitial, getLogoUrl } from "../../utils/getLogoUrl";
import NodisplayComponent from "../../components/common/NodisplayComponent";

import inxitsIcon from "../../assets/images/inxitsicon.svg"
const BrokerListingContainer = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const isStocks = category === "stocks";
  const isMutualFunds = category === "mutual-funds";
  const isEtf = category === "etf";

  const {
    data: apiMutualFundsData,
    isLoading: isMFLoading,
    isFetching: isMFFetching,
    isError: isMFError,
  } = useGetInvestmentPortfolioMutualFundsQuery(undefined, {
    skip: !isMutualFunds,
  });

  const { data: portfolioSummaryData } = useGetPortfolioSummaryQuery();

  const mutualFundsData = useMemo(() => {
    if (!isMutualFunds) return apiMutualFundsData;
    return parseMutualFundsBrokerListing(apiMutualFundsData || {}, portfolioSummaryData?.result);
  }, [apiMutualFundsData, portfolioSummaryData, isMutualFunds]);

  const {
    data: stocksData,
    isLoading: isStocksLoading,
    isFetching: isStocksFetching,
    isError: isStocksError,
  } = useGetInvestmentPortfolioStocksQuery(undefined, {
    skip: !isStocks,
  });

  const {
    data: etfData,
    isLoading: isEtfLoading,
    isFetching: isEtfFetching,
    isError: isEtfError,
  } = useGetInvestmentPortfolioEtfQuery(undefined, {
    skip: !isEtf,
  });

  const {
    data: invitData,
    isLoading: isInvitLoading,
    isFetching: isInvitFetching,
    isError: isInvitError,
  } = useGetInvestmentPortfolioInvitQuery(undefined, {
    skip: category !== "invit",
  });

  const {
    data: reitData,
    isLoading: isReitLoading,
    isFetching: isReitFetching,
    isError: isReitError,
  } = useGetInvestmentPortfolioReitQuery(undefined, {
    skip: category !== "reit",
  });

  const loading = isMutualFunds
    ? (isMFLoading || isMFFetching)
    : isStocks
      ? (isStocksLoading || isStocksFetching)
      : isEtf
        ? (isEtfLoading || isEtfFetching)
        : category === "invit"
          ? (isInvitLoading || isInvitFetching)
          : (isReitLoading || isReitFetching);

  const isError = isMutualFunds
    ? isMFError
    : isStocks
      ? isStocksError
      : isEtf
        ? isEtfError
        : category === "invit"
          ? isInvitError
          : isReitError;

  const currentCategoryData = isMutualFunds ? mutualFundsData : isStocks ? stocksData : isEtf ? etfData : category === "invit" ? invitData : reitData;

  // Map URL category to data key
  const categoryKeyMap = {
    "mutual-funds": "Mutual Funds",
    stocks: "STOCKS",
    etf: "ETF",
    invit: "InvIT",
    reit: "REITS",
  };

  const categoryDisplayName =
    categoryKeyMap[category] ||
    category.charAt(0).toUpperCase() + category.slice(1);

  const listingData = currentCategoryData || { title: categoryDisplayName, items: [] };
  const headerTitle = ["stocks", "etf", "invit", "reit"].includes(category)
    ? String(listingData.title || categoryDisplayName).toUpperCase()
    : listingData.title;

  const headerTotalValue = currentCategoryData?.totalValue || "₹0";
  const headerAsOfDate = currentCategoryData?.asOfDate || "-";

  const handleBrokerClick = (broker) => {
    if (broker.name.toLowerCase() === "inxits") {
      navigate(ROUTES.PORTFOLIO);
      return;
    }
    if (broker.route) {
      navigate(broker.route);
      return;
    }
    const brokerId = broker.id;
    navigate(
      ROUTES.INVESTMENT_PORTFOLIO_BROKER.replace(":category", category).replace(
        ":brokerId",
        brokerId,
      ),
      {
        state: {
          brokerName: broker.name,
        },
      },
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", pb: 4 }}>
      <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2 } }} disableGutters>
        {/* Header Summary Section */}
        <Box
          sx={{
            backgroundColor: "#13172a",
            borderRadius: "24px",
            p: { xs: 2.25, md: 4 },
            border: "1px solid rgba(255, 255, 255, 0.05)",
            mb: 3,
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.3)",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "flex-start" }}
            sx={{ mb: 1.5, gap: { xs: 2.5, md: 0 } }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 0.5, md: 2 }}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: { xs: 0.5, md: 0 } }}
              >
                <Box
                  component="img"
                  src={CircleIcon}
                  sx={{ width: 24, height: 24 }}
                />
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: 600,
                    color: "white",
                    letterSpacing: "0.5px",
                  }}
                >
                  {headerTitle}
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "rgba(255, 255, 255, 0.4)",
                  pl: { xs: 4.5, md: 0 },
                }}
              >
                {loading ? <Skeleton width={160} /> : headerAsOfDate}
              </Typography>
            </Stack>

            <Stack alignItems={{ xs: "flex-start", md: "flex-end" }}>
              <Typography
                sx={{
                  fontSize: { xs: "13px", md: "12px" },
                  color: "rgba(255, 255, 255, 0.4)",
                  mb: 0.2,
                  fontWeight: 500
                }}
              >
                Total Value
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "22px", md: "20px" },
                  fontWeight: 700,
                  color: "white",
                  fontFamily: "'Outfit', sans-serif"
                }}
              >
                {loading ? <Skeleton width={120} /> : headerTotalValue}
              </Typography>
            </Stack>
          </Stack>
          {/* Subtitle and Listing */}
          {listingData.subtitle && (
            <Typography
              variant="caption"
              color="rgba(255, 255, 255, 0.6)"
              sx={{ mb: 1.5, px: 1.5, display: "block" }}
            >
              {listingData.subtitle}
            </Typography>
          )}

          <Box sx={{ mt: 1 }}>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={80}
                  sx={{ borderRadius: "16px", mb: 2, bgcolor: "rgba(255,255,255,0.05)" }}
                />
              ))
            ) : (isError || !listingData?.items?.length) ? (
              <NodisplayComponent
                title={isError ? "Error Loading Brokers" : "No Brokers Found"}
                subtitle={isError ? "We couldn't retrieve the broker list. Please try again later." : `No ${categoryDisplayName.toLowerCase()} brokers connected yet.`}
                containerSx={{ minHeight: "300px" }}
              />
            ) : (
              listingData.items.map((broker) => (
                <FinFactorBankCard
                  key={broker.id}
                  logo={broker.name.toLowerCase() === "inxits" ? inxitsIcon : getLogoUrl(broker)}
                  logoSx={broker.name.toLowerCase() === "inxits" ? { p: 0, backgroundColor: "transparent" } : {}}
                  fallbackIcon={getFundInitial(broker)}
                  accountNumber={
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight="600"
                        color="white"
                        sx={{ fontSize: { xs: "0.95rem", md: "1rem" } }}
                      >
                        {broker.name}
                      </Typography>
                      {(broker.accountId || broker.brokerId) && (
                        <Typography
                          variant="caption"
                          color="rgba(255, 255, 255, 0.4)"
                          sx={{ fontSize: "12px" }}
                        >
                          {broker.accountId || broker.brokerId}
                        </Typography>
                      )}
                    </Box>
                  }
                  nomineeStatus={broker.nomineeStatus || "not registered"}
                  balance={
                    <Stack 
                      direction={{ xs: "row", md: "column" }} 
                      justifyContent="space-between" 
                      alignItems={{ xs: "center", md: "flex-end" }} 
                      spacing={0.5}
                      sx={{ width: "100%" }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="700"
                        color="#38bdf8"
                        sx={{ fontSize: { xs: "16px", md: "16px" } }}
                      >
                        {broker.value}
                      </Typography>
                      {broker.changeValue && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          {parseFloat(String(broker.changeValue).replace(/[%]/g, '')) !== 0 && (
                            broker.isPositive ? (
                              <ArrowDropUpIcon
                                sx={{
                                  fontSize: { xs: 16, md: 18 },
                                  color: "#10B981",
                                }}
                              />
                            ) : (
                              <ArrowDropDownIcon
                                sx={{
                                  fontSize: { xs: 16, md: 18 },
                                  color: "#EF4444",
                                }}
                              />
                            )
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              color: (broker.changeValue === "-" || parseFloat(String(broker.changeValue).replace(/[%]/g, '')) === 0) ? "#fff" : (broker.isPositive ? "#10B981" : "#EF4444"),
                              fontWeight: 600,
                              fontSize: { xs: "11px", md: "12px" },
                            }}
                          >
                            {broker.changeValue}{" "}
                            <span style={{ color: "#fff", fontWeight: 400 }}>
                              {broker.changeLabel}
                            </span>
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  }
                  onClick={() => handleBrokerClick(broker)}
                  backgroundColor="rgba(255, 255, 255, 0.03)"
                  sx={{
                    mb: 2,
                    borderRadius: "16px",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.06)",
                    },
                  }}
                />
              ))
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BrokerListingContainer;
